import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Card, Chip, IconButton, CardContent, Grid, Tooltip as TooltipCustom, useMediaQuery, Backdrop, CircularProgress, useTheme, Stack } from '@mui/material';
import { Menu as MenuIcon, Help as HelpIcon } from '@mui/icons-material';
// app UI controller for toggling sidenav
import { useMaterialUIController, setMiniSidenav } from "context";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import SouthIcon from '@mui/icons-material/South';
import datadrilldown from "assets/images/selectedicons/DataDrillDown.svg";
import drillDownArrow from "assets/images/selectedicons/DrillDownArrow.svg";
import FilterPanelContainer from "components/Filter/Filterpopup"
import KpiMetricscards from "components/KPI/KpiMetricscards";
import TimeSeriescards from "components/TimeSeries/TimeSeriescards";
import HighlightsCards from "components/Highlights/Highlightscard";
import Visualizations from "components/Visualizations/Visualizations";
import PieLegendCard from "components/Visualizations/Piecharts"
import Barcharts from "components/Visualizations/Barcharts"
import LineChartSubBrand from "components/Visualizations/Linechartsubbrand";
import LineChartsale from "components/Visualizations/LineChartsale"
import InventoryCharts from "components/Visualizations/Inventorycharts";
import WaterfallChart from "components/Visualizations/waterfallchart";
import TopSkuBarChart from "components/Visualizations/SkuBarchart";
import SkuSingleBarChart from "components/Visualizations/SingleBarchartSku";
import HiringFunnel from "components/Visualizations/Funnelchart";
import FilterChip from "components/FilterChip/FilterChip";

const HumanResourcesDashboard = ({ subcategoryToken: propSubcategoryToken }) => {
    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav } = controller;
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Below 900px
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // Between 900px and 1200px

    // State for API data
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const header = document.querySelector('.MuiAppBar-root');
        if (header) {
            header.style.display = 'none';
        }
    }, []);

    // Toggle sidenav from the page menu icon
    const isTogglingSidenavRef = useRef(false);
    const handleToggleSidenav = useCallback(() => {
        if (isTogglingSidenavRef.current) return;
        isTogglingSidenavRef.current = true;
        // Defer to next frame to avoid blocking heavy charts paint
        requestAnimationFrame(() => {
            setMiniSidenav(dispatch, !miniSidenav);
            setTimeout(() => {
                isTogglingSidenavRef.current = false;
            }, 150);
        });
    }, [dispatch, miniSidenav]);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
            } catch (error) {
                console.error('Failed loading static mocks or transforming data:', error);
                setError('Failed to load static mock data. Check competitive_analysis/data/allMocks.json');
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, [location.search, propSubcategoryToken]);

    // KPI cards data
    const Kpiscardsdata = [
        {
            title: "Revenue Per Employee",
            values: [
                { label: "MTD", change: "1.8%", value: "$27.8K", status: "increase" },
                { label: "QTD", change: "3.2%", value: "$81.2K", status: "increase" },
                { label: "YTD", change: "4.6%", value: "$302.7K", status: "increase" }
            ]
        },
        {
            title: "Employee Turnover Rate",
            values: [
                { label: "MTD", change: "0.2%", value: "1.4%", status: "increase" },
                { label: "QTD", change: "0.3%", value: "4.3%", status: "increase" },
                { label: "YTD", change: "0.5%", value: "17.8%", status: "increase" }
            ]
        },
        {
            title: "Safety Incident Rate (TRIR)",
            values: [
                { label: "MTD", change: "0.1%", value: "0.42", status: "increase" },
                { label: "QTD", change: "0.2%", value: "1.31", status: "increase" },
                { label: "YTD", change: "0.4%", value: "5.62", status: "decrease" }
            ]
        },
        {
            title: "Time to Fill (Days)",
            values: [
                { label: "MTD", change: "2.0%", value: "38", status: "increase" },
                { label: "QTD", change: "3.7%", value: "41", status: "increase" },
                { label: "YTD", change: "4.1%", value: "47", status: "decrease" }
            ]
        },
        {
            title: "Internal Fill Rate",
            values: [
                { label: "MTD", change: "0.1%", value: "26%", status: "increase" },
                { label: "QTD", change: "0.2%", value: "24%", status: "increase" },
                { label: "YTD", change: "1.1%", value: "21%", status: "increase" }
            ]
        },
        {
            title: "Employee Engagement Score",
            values: [
                { label: "MTD", change: "1.2%", value: "77", status: "increase" },
                { label: "QTD", change: "1.7%", value: "76", status: "increase" },
                { label: "YTD", change: "2.0%", value: "74", status: "decrease" }
            ]
        }
    ];


    //second section cards
    const timeSeriesCardsdata = [
        {
            title: "Revenue Per Employee",
            values: [
                { label: "Nov 2024", change: "+8.6%", value: "$25.6K", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: null, value: "$27.8K", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+2.6%", value: "$27.1K", status: "increase", arrow: "left" }
            ]
        },
        {
            title: "Employee Turnover Rate",
            values: [
                { label: "Nov 2024", change: "-0.4%", value: "1.8%", status: "decrease", arrow: "right" },
                { label: "Nov 2025", change: null, value: "1.4%", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+0.1%", value: "1.5%", status: "decrease", arrow: "left" }
            ]
        },
        {
            title: "Safety Incident Rate (TRIR)",
            values: [
                { label: "Nov 2024", change: "-23.6%", value: "0.55", status: "decrease", arrow: "right" },
                { label: "Nov 2025", change: null, value: "0.42", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "-8.7%", value: "0.46", status: "decrease", arrow: "left" }
            ]
        },
        {
            title: "Time to Fill (Days)",
            values: [
                { label: "Nov 2024", change: "-15.6%", value: "45", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: null, value: "38", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "-5%", value: "40", status: "increase", arrow: "left" }
            ]
        },
        {
            title: "Internal Fill Rate",
            values: [
                { label: "Nov 2024", change: "+18.2%", value: "22%", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: null, value: "26%", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+4%", value: "25%", status: "increase", arrow: "left" }
            ]
        },
        {
            title: "Employee Engagement Score",
            values: [
                { label: "Nov 2024", change: "+5.5%", value: "73", status: "increase", arrow: "right" },
                { label: "Nov 2025", change: null, value: "77", status: "nochange", arrow: null },
                { label: "Oct 2025", change: "+1.3%", value: "76", status: "increase", arrow: "left" }
            ]
        }
    ];


    const [openFilters, setOpenFilters] = useState(false);
    const handleToggleFilters = () => {
        setOpenFilters((prev) => !prev);
    };

    // Filter state
    const [activeFilter, setActiveFilter] = useState('All');
    const [filterLoading, setFilterLoading] = useState(false);

    // Define filter presets for each quick filter
    const getFilterPreset = useCallback((filterName) => {
        const allCompanies = ["Nutrabolt", "Bloom"];
        const allCategories = ["Functional Beverage", "Active Nutrition"];
        const allBrands = ["C4", "Cellucor", "XTEND", "Bloom"];
        const allSubBrands = ["C4", "C4 Performance", "C4 Smart Energy", "C4 Ultimate", "XTEND", "Bloom Sparkling Energy", "Bloom Pop"];
        const allRoutes = ["Ecommerce", "Direct", "DSD"];
        const allChannels = ["Ecommerce", "Convenience Chain", "Convenience Independent", "Drug", "Food", "Multi Outlet", "Mass"];

        switch (filterName) {
            case 'All':
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            default:
                return null;
        }
    }, []);

    const [filterPresets, setFilterPresets] = useState(() => getFilterPreset('All'));

    // Handle filter click with full-page loader
    const handleFilterClick = (filter) => {
        if (filter === activeFilter || filterLoading) return;

        setFilterLoading(true);
        setLoading(true); // Show full-page loader

        // Simulate loading delay
        setTimeout(() => {
            const preset = getFilterPreset(filter);
            setActiveFilter(filter);

            // Set the preset - this will trigger FilterPanelContainer to apply it
            if (preset) {
                setFilterPresets(preset);
            } else {
                setFilterPresets(null);
            }

            setFilterLoading(false);
            setLoading(false); // Hide full-page loader

            // Open filter panel for "Custom" filter
            if (filter === 'Custom') {
                setOpenFilters(true);
            }
        }, 800);
    };

    // Handle filter changes from FilterPanelContainer
    const handleFiltersChange = useCallback((newFilters) => {
        // Don't check for preset match if we're already in Custom mode
        // FilterPanelContainer's isApplyingPresetRef prevents callback when applying presets
        if (activeFilter === 'Custom') return;

        // Check if the current filters match any preset
        const presetNames = ['All'];
        let matchedPreset = null;

        for (const presetName of presetNames) {
            const preset = getFilterPreset(presetName);
            if (preset) {
                const isMatching = Object.keys(preset).every(key => {
                    const presetValues = [...(preset[key] || [])].sort();
                    const currentValues = [...(newFilters[key] || [])].sort();
                    return presetValues.length === currentValues.length &&
                        presetValues.every((val, idx) => val === currentValues[idx]);
                });

                if (isMatching) {
                    matchedPreset = presetName;
                    break;
                }
            }
        }

        // If filters don't match any preset, switch to Custom
        if (!matchedPreset && activeFilter !== 'Custom') {
            setActiveFilter('Custom');
            setFilterPresets(null);
        }
    }, [activeFilter, getFilterPreset]);
    const [activeIdx, setActiveIdx] = React.useState(0);
    const kpiBriefData = [
        {
            title: "Month",
            value: "$ 65.2 M",
            lyValue: "59.12 M",
            pct: "5.8%",
            positive: true,
            status: "increase",
            glvalue: "$ 85.5 M"
        },
        {
            title: "Quarter",
            value: "$ 180.6 M",
            lyValue: "160.29 M",
            pct: "11.9%",
            positive: true,
            status: "increase",
            glvalue: "$ 207.3 M"
        },
        {
            title: "Year",
            value: "$ 859.1 M",
            lyValue: "823.03 M",
            pct: "5.6%",
            positive: true,
            status: "increase",
            glvalue: "$ 882.8 M"
        }
    ];

    const headcountByDepartment = [

        { label: "Finance", value: 18, color: "#2E3BAB" },
        { label: "Operations", value: 17, color: "#3FAF6C" },
        { label: "Product", value: 9, color: "#F2A93B" },
        { label: "IT", value: 9, color: "#64719A" },
        { label: "HR", value: 6, color: "#8B5CF6" },
        { label: "Marketing", value: 23, color: "#E46BAA" },
        { label: "Commercial", value: 20, color: "#4A63E0" },
    ];
    const retentionRate = [
        { label: "Involuntary Departures", value: 4, color: "#EB6383" },
        { label: "Retained Employees", value: 88, color: "#45B97C" },
        { label: "Voluntary Departures", value: 8, color: "#F4A62A" },




    ];
    const tenureMix = [
        { label: "< 1 year", value: 35, color: "#B0C9FF" },
        { label: "3–5 years", value: 16, color: "#2E4ACF" },
        { label: "10+ years", value: 5, color: "#000000" },
        { label: "5–10 years", value: 10, color: "#1A2B8F" },
        { label: "1–3 years", value: 34, color: "#6A7FDB" },


    ];
    const hiringSource = [
        { label: "Job Boards", value: 25, color: "#6A7FDB" },
        { label: "Recruiting Agencies", value: 24, color: "#A26FDC" },
        { label: "Employee Referrals", value: 13, color: "#45B97C" },
        { label: "Company Website", value: 25, color: "#F4A62A" },
        { label: "Social", value: 14, color: "#EB6383" }
    ];

    const employeeCountByDept = {
        title: "Employee Count Stacked by Department",

        xLabels: [
            "Dec. 2025", "Jan. 2025", "Feb. 2025", "Mar. 2025",
            "Apr. 2025", "May. 2025", "Jun. 2025", "Jul. 2025",
            "Aug. 2025", "Sep. 2025", "Oct. 2025", "Nov. 2025"
        ],

        series: [
            { label: "Marketing", color: "#EB6383" },
            { label: "Commercial", color: "#6A7FDB" },
            { label: "Finance", color: "#2E4ACF" },
            { label: "Operations", color: "#45B97C" },
            { label: "Product", color: "#F4A62A" },
            { label: "IT", color: "#5F6368" },
            { label: "HR", color: "#A26FDC" }
        ],

        // Extracted from your chart image values (top totals included)
        dataMatrix: [
            // Marketing, Commercial, Finance, Operations, Product, IT, HR
            [165, 85, 20, 18, 12, 15, 8],   // Dec = 323
            [145, 110, 25, 20, 14, 35, 8],  // Jan = 357
            [35, 165, 25, 18, 13, 45, 11],  // Feb = 312
            [130, 95, 22, 20, 13, 24, 10],  // Mar = 314
            [185, 70, 24, 22, 14, 14, 5],   // Apr = 334
            [195, 75, 26, 23, 15, 25, 10],  // May = 369
            [155.03, 79.86, 20.67, 18.79, 11.27, 18.79, 6.59],
            [110, 115, 24, 22, 15, 46, 10], // Jul = 342
            [95, 120, 26, 23, 14, 18, 5],   // Aug = 301
            [80, 155, 28, 26, 16, 68, 26],  // Sep = 399
            [60, 165, 32, 28, 18, 75, 36],  // Oct = 414
            [55, 190, 35, 30, 20, 65, 30]   // Nov = 425
        ]
    };

    const toprolesdata = {
        "title": "Application Count for Top 10 Open Roles",
        "yLabels": [
            "Sales Representative",
            "Distribution Associate",
            "Customer Service",
            "Marketing Coordinator",
            "Merchandiser",
            "Data Analyst",
            "Marketing Specialist",
            "Sales Manager",
            "Content Creator",
            "Compliance Tech"
        ],
        "series": [
            { "label": "Job Boards", "color": "#4E5FE3" },
            { "label": "Recruiting Agencies", "color": "#8B5CF6" },
            { "label": "Employee Referrals", "color": "#3CB371" },
            { "label": "Company Website", "color": "#F4A62A" },
            { "label": "Social", "color": "#EC6BAA" }
        ],
        "dataMatrix": [
            [40, 30, 45, 55, 103],   // Sales Representative → 273
            [140, 70, 20, 12, 20],  // Distribution Associate → 262
            [80, 75, 30, 35, 11],   // Customer Service → 231
            [110, 15, 25, 61],      // Marketing Coordinator → 211
            [35, 45, 50, 20, 42],   // Merchandiser → 192
            [45, 60, 20, 18, 40],   // Data Analyst → 183
            [40, 85, 25, 22],       // Marketing Specialist → 172
            [40, 50, 25, 30, 16],   // Sales Manager → 161
            [35, 45, 20, 25, 14],   // Content Creator → 139
            [30, 35, 15, 10, 23]    // Compliance Tech → 113
        ]
    }

    const hiringFunnelData = {
        title: "Hiring Funnel",
        data: [
            { label: "Applications", value: 15287, color: "#C95D54" },
            { label: "Resume Qualified", value: 3398, color: "#D9AD63" },
            { label: "Interviewed", value: 887, color: "#5DAAD2" },
            { label: "Offer Sent", value: 201, color: "#5A74A8" },
            { label: "Hired", value: 113, color: "#4BAA8A" }
        ]
    };
    return (

        <Box sx={{
            ml: 0,
            transition: "margin-left 0.25s ease",
            "& .MuiBox-root": { backgroundImage: "none" }
        }} style={{

            padding: isMobile ? '8px' : '0px 16px 0px 16px',
            fontFamily: 'inter !important'
        }}>
            {/* Loading Backdrop */}
            <Backdrop
                open={loading}
                sx={{
                    position: 'fixed',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
            >
                <CircularProgress color="primary" />
            </Backdrop>


            <Box
                sx={{
                    height: {
                        xs: "calc(100dvh - 140px)", // mobile header
                        md: "calc(100vh - 64px)",  // desktop header
                    },
                    overflowY: "auto",
                    // px: isMobile ? 1 : 2,
                    pb: "100px",
                    /* smooth scrolling */
                    scrollBehavior: "smooth",
                    WebkitOverflowScrolling: "touch", // iOS momentum

                    /* hide scrollbar */
                    scrollbarWidth: "none",          // Firefox
                    msOverflowStyle: "none",         // IE / Edge
                    "&::-webkit-scrollbar": {
                        display: "none",               // Chrome / Safari
                    },
                }}
            >
                {/* Section Title: KPI's */}
                <Typography sx={{ mb: 1, mt: 3 }}>
                    <span style={{ color: '#1672B980', fontSize: '16px', fontWeight: 200, }}>Human Resources / </span> <span style={{ fontWeight: 200, fontSize: "16px" }}>KPI's</span>
                </Typography>
                <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                <KpiMetricscards cardsData={Kpiscardsdata} kpiBriefData={kpiBriefData} />

                {/* Section Title: Time Series */}
                <Typography sx={{ fontSize: '16px', mb: 1, mt: 3 }}>
                    <span style={{ color: '#1672B980', fontSize: '16px', fontWeight: 200 }}>Human Resources / </span> <span style={{ fontWeight: 200, fontSize: "16px" }}>Time Series</span>
                </Typography>
                <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                <TimeSeriescards timeSeriesCards={timeSeriesCardsdata} />

                <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontSize: '16px' }}>
                            <span style={{ color: "#1672B980", fontWeight: 200, fontSize: "16px" }}>Human Resources / </span> <span style={{ fontWeight: 200, fontSize: "16px" }}>Visualizations</span>
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {["YTD", "QTD", "MTD"].map((label, idx) => (
                                <FilterChip
                                    key={label}
                                    label={label}
                                    idx={idx}
                                    activeIdx={activeIdx}
                                    onClick={setActiveIdx}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                </Box>
                <Grid
                    container
                    rowSpacing={2}
                    columnSpacing={2}
                    sx={{
                        mt: "26px !important",
                        alignItems: "stretch"   // keeps all cards same height
                    }}
                >
                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "0px !important", sm: "0px !important", md: "0px !important" }
                    }}>
                        <PieLegendCard title="Headcount by Department" data={headcountByDepartment} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }}>
                        <PieLegendCard title="Retention Rate" data={retentionRate} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }}>
                        <PieLegendCard title="Tenure Mix" data={tenureMix} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }}>
                        <PieLegendCard title="Hiring Source" data={hiringSource} />
                    </Grid>
                </Grid>
                <Grid container spacing={3} sx={{ mt: "10px !important" }}>
                    {/* LEFT CARD - USA MAP */}
                    <Grid item xs={12} md={6} >
                        <HiringFunnel
                            title={hiringFunnelData.title}
                            data={hiringFunnelData.data}
                        />

                    </Grid>

                    {/* RIGHT CARD - BAR CHART */}
                    <Grid item xs={12} md={6}>
                        <SkuSingleBarChart
                            title={toprolesdata.title}
                            yLabels={toprolesdata.yLabels}
                            series={toprolesdata.series}
                            dataMatrix={toprolesdata.dataMatrix}
                            xTicks={[0, 50, 100, 150, 200, 250, 300]}
                            showUnit={false}
                        />
                    </Grid>
                </Grid>
                <Grid container sx={{ mt: "34px !important" }}>
                    <Grid item xs={12}>
                        <Barcharts
                            title={employeeCountByDept.title}
                            xLabels={employeeCountByDept.xLabels}
                            series={employeeCountByDept.series}
                            dataMatrix={employeeCountByDept.dataMatrix}
                            yTicks={[0, 100, 200, 300, 400, 500]}
                            rounded={false}
                            yAxisLabel="Count"
                        />
                    </Grid>
                </Grid>

            </Box>


        </Box>


    );
};

export default HumanResourcesDashboard;